import type { CSSProperties } from 'react';
import { applyPhotoPlacement } from '@fotoboek/geometry';
import type { PageElement } from '@fotoboek/domain';
import type { SceneItem } from '../editor/canvas/scene';
import type { AssetRuntime } from '../../state/projectStore';

function renderElement(el: PageElement, assetsRuntime: Record<string, AssetRuntime>) {
  const style: CSSProperties = {
    position: 'absolute',
    left: `${el.x}mm`,
    top: `${el.y}mm`,
    width: `${el.width}mm`,
    height: `${el.height}mm`,
    transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
    opacity: el.opacity,
  };

  if (el.hidden) return null;

  if (el.type === 'photoFrame') {
    const asset = el.assetId ? assetsRuntime[el.assetId] : undefined;
    return (
      <div
        key={el.id}
        style={{
          ...style,
          overflow: 'hidden',
          borderRadius: `${el.cornerRadiusMm}mm`,
          background: asset ? undefined : '#e5e0d6',
        }}
      >
        {asset ? <PhotoImage el={el} asset={asset} /> : null}
      </div>
    );
  }

  if (el.type === 'text') {
    return (
      <div
        key={el.id}
        style={{
          ...style,
          fontFamily: el.fontFamily,
          fontSize: `${el.fontSizePt}pt`,
          fontWeight: el.fontWeight,
          fontStyle: el.fontStyle,
          lineHeight: el.lineHeight,
          letterSpacing: `${el.letterSpacingEm}em`,
          textAlign: el.align,
          color: el.color,
          backgroundColor: el.backgroundColor ?? undefined,
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
        }}
      >
        {el.content}
      </div>
    );
  }

  if (el.type === 'shape') {
    if (el.shape === 'line') {
      return (
        <div
          key={el.id}
          style={{
            ...style,
            height: `${Math.max(el.strokeWidthMm, 0.3)}mm`,
            background: el.stroke ?? el.fill ?? '#1a1a1a',
          }}
        />
      );
    }
    return (
      <div
        key={el.id}
        style={{
          ...style,
          background: el.fill ?? 'transparent',
          border: el.stroke ? `${el.strokeWidthMm}mm solid ${el.stroke}` : undefined,
          borderRadius: el.shape === 'ellipse' ? '50%' : `${el.cornerRadiusMm}mm`,
        }}
      />
    );
  }

  return <div key={el.id} style={{ ...style, background: el.color }} />;
}

function PhotoImage({
  el,
  asset,
}: {
  el: Extract<PageElement, { type: 'photoFrame' }>;
  asset: AssetRuntime;
}) {
  const placed = applyPhotoPlacement(
    { width: el.width, height: el.height },
    { width: asset.widthPx, height: asset.heightPx },
    el.placement,
  );
  return (
    <img
      src={asset.objectUrl}
      alt=""
      style={{
        position: 'absolute',
        left: `${placed.x}mm`,
        top: `${placed.y}mm`,
        width: `${placed.width}mm`,
        height: `${placed.height}mm`,
        transform:
          `${placed.flipX ? 'scaleX(-1)' : ''} ${placed.flipY ? 'scaleY(-1)' : ''}`.trim() ||
          undefined,
      }}
    />
  );
}

export function StaticPageRenderer({
  item,
  assetsRuntime,
}: {
  item: SceneItem;
  assetsRuntime: Record<string, AssetRuntime>;
}) {
  return (
    <div
      className="static-page"
      style={{
        position: 'relative',
        width: `${item.sizeMm.width}mm`,
        height: `${item.sizeMm.height}mm`,
        background: item.backgroundColor,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {[...item.elements]
        .sort((a, b) => a.zIndex - b.zIndex)
        .map((el) => renderElement(el, assetsRuntime))}
    </div>
  );
}
